import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import VatAdvancesDataService from '../../Services/vatAdvancesData.service';
import ReportingFollowUpService from '../../Services/reportingFollowUp.service';
import ReportsService from '../../Services/reports.service';
import PartnerService from '../../Services/partner.service';
import ClientService from '../../Services/client.service';
import { calculateIncomeTaxAdvances } from '../../Utils/advanceCalculator/calculateIncomeTaxAdvances';
import { roundVatToInteger } from '../../Utils/FormatNumber';
import {
    FOLLOW_UP_SECTIONS,
    MANUAL_KEYS,
    REPORTING_FOLLOW_UP_SAVED_EVENT,
    getSectionPeriod,
    getSectionReportingType,
    isBlankValue,
    reportDiffers,
    toDateInputValue,
    toNumber,
    todayDateString,
} from './reportingFollowUpConstants';

const NO_PARTNER = 'main';

export const trackingKey = (section, partnerId = null) =>
    partnerId ? `${section}:${partnerId}` : String(section);

const toServerDate = (dateString) => (dateString ? `${dateString}T00:00:00` : null);

const EMPTY_GUID = '00000000-0000-0000-0000-000000000000';

/** GetVatByDateRange / GetAdvanceByDateRange return a zero-filled placeholder (Id = Guid.Empty) when nothing was reported. */
const hasRecord = (record) => !!record?.id && record.id !== EMPTY_GUID;

const sumBy = (items, pick) => items.reduce((total, item) => total + (Number(pick(item)) || 0), 0);

const vatBooksFromPageData = (data, client, user) => {
    const payload = VatAdvancesDataService.buildReduxPayload(data, client || {}, user || {});
    return {
        incomesNoZeroVats: payload.incomeNoVat || 0,
        incomesNoVat: (payload.incomesAmount || 0) - (payload.incomeNoVat || 0),
        vatTransactions: payload.incomesVatAmount || 0,
        equipmentVAT: payload.purchaseOfEquipmentExpensesVatAmount || 0,
        otherVATTaxes: payload.expensesVatAmount || 0,
    };
};

const VAT_REPORT_FIELDS = ['incomesNoZeroVats', 'incomesNoVat', 'vatTransactions', 'equipmentVAT', 'otherVATTaxes'];

const vatBooksAsReport = (books) =>
    Object.fromEntries(VAT_REPORT_FIELDS.map((field) => [field, String(roundVatToInteger(books[field]))]));

const advanceBooksAsReport = ({ books, settings, report }) => ({
    ...report,
    witholdingIncomesNoVat: String(roundVatToInteger(books.turnover)),
    incomeTaxAdvancePercentage: settings.usePercentage ? String(settings.percentage) : '',
    incomeTaxAdvanceAmount: settings.usePercentage ? '' : String(roundVatToInteger(books.advanceAmount)),
    acceptedWithholdingTax: String(roundVatToInteger(books.withholding)),
});

const isBlank = isBlankValue;

const toInputString = (value, round = false) => {
    if (value === null || value === undefined || value === '') return '';
    return String(round ? roundVatToInteger(value) : value);
};

const vatReportFromReported = (reported) => ({
    incomesNoZeroVats: toInputString(reported?.incomesNoZeroVats, true),
    incomesNoVat: toInputString(reported?.incomesNoVat, true),
    vatTransactions: toInputString(reported?.vatTransactions, true),
    equipmentVAT: toInputString(reported?.equipmentVAT, true),
    otherVATTaxes: toInputString(reported?.otherVATTaxes, true),
});

const ADVANCE_OPTIONS = { allowNegative: true };

/** Withholding used to offset the advance: never more than the positive part of the advance. */
const withholdingOffsetOf = (withholding, advanceTotal) => Math.min(withholding, Math.max(advanceTotal, 0));

/**
 * ReportedAdvance.AcceptedWithholdingTax holds the withholding used for the offset (capped at the
 * advance). The uncapped reported withholding is restored from the follow-up entry while it still
 * yields that same offset; otherwise the report changed elsewhere and the stored offset wins.
 */
const advanceReportFromReported = (reported, savedWithholding = null) => {
    const offset = reported?.acceptedWithholdingTax;
    let withholding = offset;
    if (reported && savedWithholding != null && offset != null) {
        const advanceTotal = Number(reported.incomeTaxAdvancePercentage) > 0
            ? (toNumber(reported.witholdingIncomesNoVat) * Number(reported.incomeTaxAdvancePercentage)) / 100
            : toNumber(reported.incomeTaxAdvanceAmount);
        if (Math.round(withholdingOffsetOf(savedWithholding, advanceTotal)) === Math.round(toNumber(offset))) {
            withholding = savedWithholding;
        }
    }
    return {
        witholdingIncomesNoVat: toInputString(reported?.witholdingIncomesNoVat, true),
        incomeTaxAdvancePercentage: toInputString(reported?.incomeTaxAdvancePercentage),
        incomeTaxAdvanceAmount: toInputString(reported?.incomeTaxAdvanceAmount, true),
        acceptedWithholdingTax: toInputString(withholding, true),
        surplusExpenses: reported?.surplusExpenses ?? 0,
    };
};

const entryToTracking = (entry) => ({
    paymentMethod: entry?.paymentMethod ?? 0,
    followUpStatus: entry?.followUpStatus ?? 0,
    reportDate: toDateInputValue(entry?.reportDate),
    paymentDate: toDateInputValue(entry?.paymentDate),
    note: entry?.note ?? '',
    manualValues: Object.fromEntries(
        Object.entries(entry?.manualValues || {}).map(([k, v]) => [k, toInputString(v)])
    ),
});

const manualValuesToNumbers = (manualValues = {}) =>
    Object.fromEntries(
        Object.entries(manualValues).map(([k, v]) => [k, v === '' || v == null ? null : toNumber(v)])
    );

/** Advance-period settings, resolved the same way ClientDetails resolves them. */
const resolveAdvanceSettings = (pageData, client, partner) => {
    if (partner) {
        const yearly = pageData?.partnerAdvanceSettings?.[partner.id];
        return {
            percentage: yearly?.incomeTaxAdvancePercentage != null
                ? Number(yearly.incomeTaxAdvancePercentage)
                : (partner.withholdingTaxPercentage || 0),
            usePercentage: yearly?.useIncomeTaxAdvancePercentage != null
                ? !!yearly.useIncomeTaxAdvancePercentage
                : !!partner.useIncomeTaxAdvancePercentage,
            fixedAmount: yearly?.incomeTaxAdvanceAmount != null
                ? Number(yearly.incomeTaxAdvanceAmount)
                : (partner.incomeTaxAdvanceAmount || 0),
        };
    }
    return {
        percentage: pageData?.incomeTaxAdvancePercentage != null
            ? Number(pageData.incomeTaxAdvancePercentage)
            : (client?.incomeTaxAdvancePercentage ?? 0),
        usePercentage: pageData?.useIncomeTaxAdvancePercentage != null
            ? !!pageData.useIncomeTaxAdvancePercentage
            : (client?.useIncomeTaxAdvancePercentage ?? true),
        fixedAmount: pageData?.incomeTaxAdvanceAmount != null
            ? Number(pageData.incomeTaxAdvanceAmount)
            : (client?.incomeTaxAdvanceAmount ?? 0),
    };
};

export const computeAdvanceReport = (values, settings) => {
    const turnover = toNumber(values.witholdingIncomesNoVat);
    const withholding = toNumber(values.acceptedWithholdingTax);
    const percentage = toNumber(values.incomeTaxAdvancePercentage);
    const calc = calculateIncomeTaxAdvances(
        turnover,
        withholding,
        percentage,
        settings.usePercentage,
        toNumber(values.incomeTaxAdvanceAmount),
        toNumber(values.surplusExpenses),
        ADVANCE_OPTIONS
    );
    const withholdingOffset = calc.AcceptedWithholdingTaxMaximum || 0;
    return {
        turnover,
        percentage,
        advanceAmount: calc.total || 0,
        withholding,
        withholdingOffset,
        excessDeductions: Math.max(withholding - withholdingOffset, 0),
        toPay: calc.IncomeTaxAdvances || 0,
    };
};

export const useReportingFollowUp = ({ open, client, user, token, year, month }) => {
    const { t } = useTranslation();
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [pageDataByCase, setPageDataByCase] = useState(null);
    const [followUp, setFollowUp] = useState(null);
    const [partners, setPartners] = useState([]);
    const [selectedPartnerId, setSelectedPartnerId] = useState(null);

    // Edits are kept apart from the loaded data; a key present here means "changed, save it".
    const [vatEdit, setVatEdit] = useState(null);
    const [advanceEdits, setAdvanceEdits] = useState({});
    const [trackingEdits, setTrackingEdits] = useState({});
    const [materialEdit, setMaterialEdit] = useState(null);
    const [feeEdit, setFeeEdit] = useState(null);

    const requestIdRef = useRef(0);
    const caseId = client?.caseId;
    const isPartnership = !!client?.isPartners;

    const reportingType = client?.reportingType;
    const advancedReportingType = client?.advancedReportingType;
    const periods = useMemo(() => {
        const types = { reportingType, advancedReportingType };
        return {
            vat: getSectionPeriod(getSectionReportingType(types, FOLLOW_UP_SECTIONS.VAT), year, month),
            advance: getSectionPeriod(getSectionReportingType(types, FOLLOW_UP_SECTIONS.INCOME_TAX_ADVANCE), year, month),
            withholding: getSectionPeriod(getSectionReportingType(types, FOLLOW_UP_SECTIONS.INCOME_TAX_WITHHOLDING), year, month),
            nationalInsurance: getSectionPeriod(null, year, month),
        };
    }, [reportingType, advancedReportingType, year, month]);

    const resetEdits = () => {
        setVatEdit(null);
        setAdvanceEdits({});
        setTrackingEdits({});
        setMaterialEdit(null);
        setFeeEdit(null);
    };

    const load = useCallback(async () => {
        if (!caseId || !token) return;
        const requestId = ++requestIdRef.current;
        setLoading(true);
        try {
            const followUpData = await ReportingFollowUpService.getPeriodData({ caseId, year, month }, token);
            if (requestId !== requestIdRef.current) return;

            // Every case of the VAT / tax file group whose page data is summed, plus the owners whose
            // reported rows and advance settings are read.
            const fetchCaseIds = [...new Set([
                ...(followUpData?.vatGroup?.fetchCaseIds || []),
                ...(followUpData?.taxGroup?.fetchCaseIds || []),
                followUpData?.vatGroup?.ownerCaseId,
                followUpData?.taxGroup?.ownerCaseId,
            ].filter(Boolean))];
            if (fetchCaseIds.length === 0) fetchCaseIds.push(caseId);

            const pages = await Promise.all(fetchCaseIds.map((id) =>
                VatAdvancesDataService.fetchVatAdvancesPageData({
                    CaseId: id,
                    StartDate: toServerDate(periods.vat.startDate),
                    EndDate: toServerDate(periods.vat.endDate),
                    IsRange: false,
                    AdvanceStartDate: toServerDate(periods.advance.startDate),
                    AdvanceEndDate: toServerDate(periods.advance.endDate),
                    AccountantId: user?.accountantId || client?.accountantId,
                    CheckType: 'תקבול',
                    OnlyProcessed: false,
                }, token)
            ));
            if (requestId !== requestIdRef.current) return;
            setPageDataByCase(Object.fromEntries(fetchCaseIds.map((id, i) => [id, pages[i]])));
            setFollowUp(followUpData);
            resetEdits();
        } catch (error) {
            if (requestId !== requestIdRef.current) return;
            console.error('Error loading reporting follow-up:', error);
            toast.error(t('ReportingFollowUpLoadFailed'));
        } finally {
            if (requestId === requestIdRef.current) setLoading(false);
        }
    }, [caseId, token, year, month, periods, user?.accountantId, client?.accountantId, t]);

    useEffect(() => {
        if (open) load();
    }, [open, load]);

    useEffect(() => {
        if (!open || !isPartnership || !caseId || !token) {
            setPartners([]);
            setSelectedPartnerId(null);
            return;
        }
        let cancelled = false;
        (async () => {
            try {
                const response = await PartnerService.getPartnersByCaseId(caseId, token);
                if (!response.ok || cancelled) return;
                const list = await response.json();
                if (cancelled) return;
                setPartners(list || []);
                setSelectedPartnerId((current) => current ?? list?.[0]?.id ?? null);
            } catch (error) {
                console.error('Error fetching partners:', error);
            }
        })();
        return () => { cancelled = true; };
    }, [open, isPartnership, caseId, token]);

    // Excess deductions carried from the tax year's earlier advance periods; loaded apart from the
    // period data because it walks every earlier period.
    const [priorExcess, setPriorExcess] = useState(null);
    const [priorExcessLoading, setPriorExcessLoading] = useState(false);
    const priorPartnerId = isPartnership ? selectedPartnerId : null;

    useEffect(() => {
        setPriorExcess(null);
        if (!open || !caseId || !token || (isPartnership && !priorPartnerId)) {
            setPriorExcessLoading(false);
            return undefined;
        }
        let cancelled = false;
        setPriorExcessLoading(true);
        ReportingFollowUpService.getPriorExcessDeductions({ caseId, year, month, partnerId: priorPartnerId }, token)
            .then((result) => { if (!cancelled) setPriorExcess(result); })
            .catch((error) => console.error('Error loading prior excess deductions:', error))
            .finally(() => { if (!cancelled) setPriorExcessLoading(false); });
        return () => { cancelled = true; };
    }, [open, caseId, token, year, month, isPartnership, priorPartnerId]);

    // ---------------------------------------------------------------- tracking fields

    const findEntry = useCallback((section, partnerId = null) => {
        const sectionData = followUp?.sections?.find((s) => s.section === section);
        return sectionData?.entries?.find((e) => (e.partnerId || null) === (partnerId || null)) || null;
    }, [followUp]);

    /** Personal NI amounts from the imported National Insurance file, used where nothing was saved. */
    const personalNiAutoValues = useMemo(() => {
        const ni = followUp?.nationalInsurance;
        if (!ni) return {};
        const saved = findEntry(FOLLOW_UP_SECTIONS.PERSONAL_NATIONAL_INSURANCE)?.manualValues || {};
        const defaults = {
            [MANUAL_KEYS.PERSONAL_NI.ownerCurrentPayment]: ni.monthlyAdvance,
            [MANUAL_KEYS.PERSONAL_NI.ownerInsuredIncome]: ni.selfEmployedIncome,
        };
        return Object.fromEntries(
            Object.entries(defaults)
                .filter(([key, value]) => value !== null && value !== undefined && isBlankValue(saved[key]))
                .map(([key, value]) => [key, toInputString(value)])
        );
    }, [followUp, findEntry]);

    const baseTracking = useCallback((section, partnerId = null) => {
        const tracking = entryToTracking(findEntry(section, partnerId));
        if (section !== FOLLOW_UP_SECTIONS.PERSONAL_NATIONAL_INSURANCE || partnerId) return tracking;
        return { ...tracking, manualValues: { ...personalNiAutoValues, ...tracking.manualValues } };
    }, [findEntry, personalNiAutoValues]);

    const getTracking = useCallback((section, partnerId = null) => {
        const key = trackingKey(section, partnerId);
        return trackingEdits[key] ?? baseTracking(section, partnerId);
    }, [trackingEdits, baseTracking]);

    const setTrackingField = useCallback((section, partnerId, field, value) => {
        const key = trackingKey(section, partnerId);
        setTrackingEdits((prev) => {
            const current = prev[key] ?? baseTracking(section, partnerId);
            return { ...prev, [key]: { ...current, [field]: value } };
        });
    }, [baseTracking]);

    const setManualValue = useCallback((section, partnerId, name, value) => {
        const key = trackingKey(section, partnerId);
        setTrackingEdits((prev) => {
            const current = prev[key] ?? baseTracking(section, partnerId);
            return {
                ...prev,
                [key]: { ...current, manualValues: { ...current.manualValues, [name]: value } },
            };
        });
    }, [baseTracking]);

    const setManualValues = useCallback((section, partnerId, values) => {
        const key = trackingKey(section, partnerId);
        setTrackingEdits((prev) => {
            const current = prev[key] ?? baseTracking(section, partnerId);
            return {
                ...prev,
                [key]: { ...current, manualValues: { ...current.manualValues, ...values } },
            };
        });
    }, [baseTracking]);

    const materialReceivedDate = materialEdit ?? toDateInputValue(followUp?.materialReceivedDate);
    const outstandingFeeBalance = feeEdit ?? toInputString(followUp?.outstandingFeeBalance, true);

    // ---------------------------------------------------------------- VAT

    const vatGroup = followUp?.vatGroup;
    const taxGroup = followUp?.taxGroup;
    const vatOwnerCaseId = vatGroup?.ownerCaseId || caseId;
    const taxOwnerCaseId = taxGroup?.ownerCaseId || caseId;
    const vatOwnerPage = pageDataByCase?.[vatOwnerCaseId] ?? null;
    const taxOwnerPage = pageDataByCase?.[taxOwnerCaseId] ?? null;

    const groupPages = useCallback((group, ownerCaseId) => {
        const ids = group?.fetchCaseIds?.length ? group.fetchCaseIds : [ownerCaseId];
        return ids.map((id) => pageDataByCase?.[id]).filter(Boolean);
    }, [pageDataByCase]);

    const reportedVat = hasRecord(vatOwnerPage?.reportedVat) ? vatOwnerPage.reportedVat : null;

    const vat = useMemo(() => {
        if (!vatOwnerPage) return null;
        const memberBooks = groupPages(vatGroup, vatOwnerCaseId).map((data) => vatBooksFromPageData(data, client, user));
        const books = Object.fromEntries(VAT_REPORT_FIELDS.map((field) => [field, sumBy(memberBooks, (b) => b[field])]));
        books.toPay = books.vatTransactions - books.equipmentVAT - books.otherVATTaxes;

        const report = vatEdit ?? vatReportFromReported(reportedVat);
        const reportToPay =
            toNumber(report.vatTransactions) - toNumber(report.equipmentVAT) - toNumber(report.otherVATTaxes);
        const hasReport = VAT_REPORT_FIELDS.some((field) => !isBlank(report[field]));

        return {
            books,
            report,
            reportToPay,
            hasReport,
            mismatch: {
                ...Object.fromEntries(VAT_REPORT_FIELDS.map((field) => [field, reportDiffers(books[field], report[field])])),
                toPay: hasReport && reportDiffers(books.toPay, reportToPay),
            },
            isReported: !!reportedVat,
            members: vatGroup?.members || [],
            businessesCount: Math.max(vatGroup?.members?.length || 0, 1),
        };
    }, [vatOwnerPage, groupPages, vatGroup, vatOwnerCaseId, reportedVat, vatEdit, client, user]);

    const setVatReportField = useCallback((field, value) => {
        setVatEdit((prev) => ({ ...(prev ?? vatReportFromReported(reportedVat)), [field]: value }));
    }, [reportedVat]);

    const copyVatBooks = useCallback(() => {
        if (vat) setVatEdit(vatBooksAsReport(vat.books));
    }, [vat]);

    // ---------------------------------------------------------------- Income tax advances

    const selectedPartner = isPartnership
        ? partners.find((p) => p.id === selectedPartnerId) || null
        : null;
    const advanceKey = selectedPartner?.id ?? NO_PARTNER;

    const reportedAdvanceFor = useCallback((partnerId) => {
        const reported = taxOwnerPage?.reportedAdvance;
        if (!reported) return null;
        if (partnerId) return hasRecord(reported[partnerId]) ? reported[partnerId] : null;
        return hasRecord(reported) ? reported : null;
    }, [taxOwnerPage]);

    const advanceReportFor = useCallback((partnerId) => {
        const saved = findEntry(FOLLOW_UP_SECTIONS.INCOME_TAX_ADVANCE, partnerId)
            ?.manualValues?.[MANUAL_KEYS.ADVANCE.reportedWithholding];
        return advanceReportFromReported(reportedAdvanceFor(partnerId), saved ?? null);
    }, [findEntry, reportedAdvanceFor]);

    const advance = useMemo(() => {
        if (!taxOwnerPage) return null;
        if (isPartnership && !selectedPartner) return null;

        const pages = groupPages(taxGroup, taxOwnerCaseId);
        const groupTurnover = sumBy(pages, (data) => data.advanceIncomesNoVat);
        const groupWithholding = sumBy(pages, (data) => data.advanceReceiptWithholding);

        const settings = resolveAdvanceSettings(taxOwnerPage, client, selectedPartner);
        const share = selectedPartner ? (Number(selectedPartner.equity) || 0) / 100 : 1;
        const turnover = selectedPartner ? Math.floor(groupTurnover * share) : groupTurnover;
        const withholding = selectedPartner ? Math.floor(groupWithholding * share) : groupWithholding;
        const calc = calculateIncomeTaxAdvances(
            turnover, withholding, settings.percentage, settings.usePercentage, settings.fixedAmount, 0, ADVANCE_OPTIONS
        );

        // Excess deductions accumulate over the tax year: the earlier periods' excess plus this period's.
        const priorBooks = priorExcess ? toNumber(priorExcess.books) : null;
        const priorReported = priorExcess ? toNumber(priorExcess.reported) : null;
        const accumulate = (periodExcess, prior) => (priorExcessLoading ? null : periodExcess + (prior ?? 0));

        const reported = reportedAdvanceFor(selectedPartner?.id);
        const report = advanceEdits[advanceKey] ?? advanceReportFor(selectedPartner?.id);
        const reportPeriod = computeAdvanceReport(report, settings);
        const reportComputed = {
            ...reportPeriod,
            periodExcessDeductions: reportPeriod.excessDeductions,
            priorExcessDeductions: priorReported,
            excessDeductions: accumulate(reportPeriod.excessDeductions, priorReported),
        };
        const withholdingOffset = calc.AcceptedWithholdingTaxMaximum || 0;
        const periodExcessDeductions = Math.max(withholding - withholdingOffset, 0);
        const books = {
            turnover,
            percentage: settings.percentage,
            advanceAmount: calc.total || 0,
            withholding,
            withholdingOffset,
            periodExcessDeductions,
            priorExcessDeductions: priorBooks,
            excessDeductions: accumulate(periodExcessDeductions, priorBooks),
            toPay: calc.IncomeTaxAdvances || 0,
        };
        const hasReport = ['witholdingIncomesNoVat', 'incomeTaxAdvancePercentage', 'incomeTaxAdvanceAmount', 'acceptedWithholdingTax']
            .some((field) => !isBlank(report[field]));
        const computedDiffers = (field) => hasReport && reportDiffers(books[field], reportComputed[field]);

        return {
            settings,
            books,
            report,
            reportComputed,
            hasReport,
            mismatch: {
                turnover: reportDiffers(books.turnover, report.witholdingIncomesNoVat),
                percentage: settings.usePercentage
                    && reportDiffers(books.percentage, report.incomeTaxAdvancePercentage, { exact: true }),
                advanceAmount: settings.usePercentage
                    ? computedDiffers('advanceAmount')
                    : reportDiffers(books.advanceAmount, report.incomeTaxAdvanceAmount),
                withholding: reportDiffers(books.withholding, report.acceptedWithholdingTax),
                withholdingOffset: computedDiffers('withholdingOffset'),
                periodExcessDeductions: computedDiffers('periodExcessDeductions'),
                priorExcessDeductions: reportDiffers(priorBooks, priorReported),
                excessDeductions: computedDiffers('excessDeductions'),
                toPay: computedDiffers('toPay'),
            },
            priorExcessLoading,
            priorReportedPeriods: priorExcess?.reportedPeriods ?? 0,
            priorPeriods: priorExcess?.periods ?? 0,
            isReported: !!reported,
            members: taxGroup?.members || [],
            businessesCount: Math.max(taxGroup?.members?.length || 0, 1),
        };
    }, [taxOwnerPage, isPartnership, selectedPartner, groupPages, taxGroup, taxOwnerCaseId, client,
        reportedAdvanceFor, advanceReportFor, advanceEdits, advanceKey, priorExcess, priorExcessLoading]);

    // ---------------------------------------------------------------- Income tax withholding

    const withholdingReported = !!followUp?.withholdingReported;

    const setAdvanceReportField = useCallback((field, value) => {
        setAdvanceEdits((prev) => {
            const current = prev[advanceKey] ?? advanceReportFor(selectedPartner?.id);
            return { ...prev, [advanceKey]: { ...current, [field]: value } };
        });
    }, [advanceKey, advanceReportFor, selectedPartner?.id]);

    const copyAdvanceBooks = useCallback(() => {
        if (!advance) return;
        setAdvanceEdits((prev) => ({
            ...prev,
            [advanceKey]: advanceBooksAsReport({ ...advance, report: prev[advanceKey] ?? advance.report }),
        }));
    }, [advance, advanceKey]);

    // ---------------------------------------------------------------- save

    const isDirty = vatEdit !== null
        || Object.keys(advanceEdits).length > 0
        || Object.keys(trackingEdits).length > 0
        || materialEdit !== null
        || feeEdit !== null;

    /**
     * Saves everything that changed. `reportVat` / `reportAdvance` also report that section's
     * period even without edits, using the Report column, or the books when it is still empty.
     */
    const save = useCallback(async ({ reportVat = false, reportAdvance = false, reportWithholding = false } = {}) => {
        const vatToSave = vatEdit ?? (reportVat && vat
            ? (VAT_REPORT_FIELDS.every((f) => isBlank(vat.report[f])) ? vatBooksAsReport(vat.books) : vat.report)
            : null);

        const advancesToSave = { ...advanceEdits };
        if (reportAdvance && advance && !advancesToSave[advanceKey]) {
            const blank = ['witholdingIncomesNoVat', 'acceptedWithholdingTax'].every((f) => isBlank(advance.report[f]));
            advancesToSave[advanceKey] = blank ? advanceBooksAsReport(advance) : advance.report;
        }

        if (!caseId || (!isDirty && !vatToSave && Object.keys(advancesToSave).length === 0 && !reportWithholding)) return;
        setSaving(true);
        try {
            // Tracking first: saving a report below stamps an empty reporting date, and that stamp
            // must not be overwritten by this call.
            const trackingToSave = { ...trackingEdits };
            if (reportWithholding) {
                const withholdingKey = trackingKey(FOLLOW_UP_SECTIONS.INCOME_TAX_WITHHOLDING);
                const current = trackingToSave[withholdingKey]
                    ?? entryToTracking(findEntry(FOLLOW_UP_SECTIONS.INCOME_TAX_WITHHOLDING));
                trackingToSave[withholdingKey] = { ...current, reportDate: current.reportDate || todayDateString() };
            }
            Object.entries(advancesToSave).forEach(([key, values]) => {
                const partnerId = key === NO_PARTNER ? null : key;
                const trackKey = trackingKey(FOLLOW_UP_SECTIONS.INCOME_TAX_ADVANCE, partnerId);
                const current = trackingToSave[trackKey]
                    ?? entryToTracking(findEntry(FOLLOW_UP_SECTIONS.INCOME_TAX_ADVANCE, partnerId));
                trackingToSave[trackKey] = {
                    ...current,
                    manualValues: {
                        ...current.manualValues,
                        [MANUAL_KEYS.ADVANCE.reportedWithholding]: isBlank(values.acceptedWithholdingTax)
                            ? ''
                            : String(toNumber(values.acceptedWithholdingTax)),
                    },
                };
            });

            const entries = Object.entries(trackingToSave).map(([key, value]) => {
                const [section, partnerId] = key.split(':');
                return {
                    section: Number(section),
                    partnerId: partnerId || null,
                    paymentMethod: value.paymentMethod ?? 0,
                    followUpStatus: value.followUpStatus ?? 0,
                    reportDate: toServerDate(value.reportDate),
                    paymentDate: toServerDate(value.paymentDate),
                    note: value.note || null,
                    manualValues: manualValuesToNumbers(value.manualValues),
                };
            });
            if (entries.length > 0 || materialEdit !== null || feeEdit !== null) {
                await ReportingFollowUpService.save({
                    caseId,
                    year,
                    month,
                    materialReceivedDate: toServerDate(materialReceivedDate),
                    outstandingFeeBalance: isBlank(outstandingFeeBalance) ? null : toNumber(outstandingFeeBalance),
                    entries,
                }, token);
            }

            if (vatToSave) {
                const response = await ReportsService.createReportedVat({
                    caseId: vatOwnerCaseId,
                    VATTransactions: toNumber(vatToSave.vatTransactions),
                    OtherVATTaxes: toNumber(vatToSave.otherVATTaxes),
                    EquipmentVAT: toNumber(vatToSave.equipmentVAT),
                    NetVAT: toNumber(vatToSave.vatTransactions) - toNumber(vatToSave.equipmentVAT) - toNumber(vatToSave.otherVATTaxes),
                    IncomesNoVat: toNumber(vatToSave.incomesNoVat),
                    IncomesNoZeroVats: toNumber(vatToSave.incomesNoZeroVats),
                    ExpensesNoVat: 0,
                    EquipmentExpenses: 0,
                    StartDate: toServerDate(periods.vat.startDate),
                    EndDate: toServerDate(periods.vat.endDate),
                }, token);
                if (!response?.ok) throw new Error(await response?.text?.());
            }

            for (const [key, values] of Object.entries(advancesToSave)) {
                const partner = key === NO_PARTNER ? null : partners.find((p) => p.id === key);
                const settings = resolveAdvanceSettings(taxOwnerPage, client, partner);
                const computed = computeAdvanceReport(values, settings);
                const response = await ReportsService.createReportedAdvance({
                    caseId: taxOwnerCaseId,
                    partnerId: partner?.id || null,
                    AcceptedWithholdingTax: computed.withholdingOffset,
                    PaymentWithholdingTax: computed.toPay,
                    IncomeTaxAdvancePercentage: settings.usePercentage ? computed.percentage : 0,
                    IncomeTaxAdvanceAmount: settings.usePercentage ? 0 : computed.advanceAmount,
                    WitholdingIncomesNoVat: computed.turnover,
                    StartDate: toServerDate(periods.advance.startDate),
                    EndDate: toServerDate(periods.advance.endDate),
                    WithholdingTaxStatus: 1,
                    surplusExpenses: toNumber(values.surplusExpenses),
                }, token);
                if (!response?.ok) throw new Error(await response?.text?.());
            }

            if (reportWithholding) {
                // Same per-month flag as the clients page "withholding reporting" column; a
                // bi-monthly period flags both of its months.
                const { startMonth, endMonth } = periods.withholding;
                for (let m = startMonth; m <= endMonth; m += 1) {
                    await ClientService.UpdateWithholdingReporting(token, caseId, year, m, true);
                }
            }

            toast.success(t('SavedSuccessfully'));
            await load();
            window.dispatchEvent(new CustomEvent(REPORTING_FOLLOW_UP_SAVED_EVENT, { detail: { caseId } }));
        } catch (error) {
            console.error('Error saving reporting follow-up:', error);
            toast.error(t('ReportingFollowUpSaveFailed'));
        } finally {
            setSaving(false);
        }
    }, [caseId, isDirty, trackingEdits, findEntry, materialEdit, materialReceivedDate, feeEdit, outstandingFeeBalance,
        vatEdit, vat, advanceEdits, advance, advanceKey, partners, vatOwnerCaseId, taxOwnerCaseId, taxOwnerPage, client,
        periods, year, month, token, t, load]);

    const reportVatPeriod = useCallback(() => save({ reportVat: true }), [save]);
    const reportAdvancePeriod = useCallback(() => save({ reportAdvance: true }), [save]);
    const reportWithholdingPeriod = useCallback(() => save({ reportWithholding: true }), [save]);

    return {
        loading,
        saving,
        isDirty,
        periods,
        followUp,
        partners,
        selectedPartnerId,
        setSelectedPartnerId,
        vat,
        setVatReportField,
        copyVatBooks,
        advance,
        setAdvanceReportField,
        copyAdvanceBooks,
        withholdingReported,
        personalNiAutoValues,
        getTracking,
        setTrackingField,
        setManualValue,
        setManualValues,
        materialReceivedDate,
        setMaterialReceivedDate: setMaterialEdit,
        outstandingFeeBalance,
        setOutstandingFeeBalance: setFeeEdit,
        save,
        reportVatPeriod,
        reportAdvancePeriod,
        reportWithholdingPeriod,
    };
};

export default useReportingFollowUp;
